import { useEffect } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { getPushBannerContent } from '../constants/pushBanner';

/**
 * Pull the deepLink out of whatever shape the native layer delivers.
 *
 * Different notification paths have historically wrapped the FCM data map at
 * different depths: a background tap may arrive as `{ data: { deepLink } }`,
 * while a cold-start tap or a manually-forwarded message can arrive flat as
 * `{ deepLink }` or double-wrapped as `{ data: { data: { deepLink } } }`.
 * The previous handler only read `data.data.deepLink`, so every shape except
 * that one resolved to `undefined` and silently aborted navigation — which is
 * why most features never opened from a notification while visitor (whose
 * payload happened to match) did. Check every known location.
 */
function extractDeepLink(data: any): string | undefined {
  const raw =
    data?.data?.deepLink ??
    data?.deepLink ??
    data?.data?.data?.deepLink ??
    data?.notification?.deepLink ??
    data?.data?.link ??
    data?.link;

  if (typeof raw !== 'string' || !raw.trim()) return undefined;

  let link = raw.trim();
  // Strip an app scheme / host if the backend sent a full URL
  // (e.g. "mycasa://app/polling" or "https://host/polling" → "/polling").
  const schemeMatch = link.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\/[^/]*(\/.*)?$/);
  if (schemeMatch) link = schemeMatch[1] || '/';
  // Ensure a leading slash so react-router treats it as an absolute path
  // (navigate('polling') is relative and would not reach the route).
  if (!link.startsWith('/')) link = `/${link}`;
  return link;
}

/**
 * Pull the `extra` payload out of the same set of possible shapes. Firebase
 * wraps the real payload as a JSON string under `extra`, sometimes nested one
 * extra level deep.
 */
function extractExtra(data: any): any {
  let extra = data?.data?.extra ?? data?.extra ?? data?.data;
  if (extra && typeof extra.extra === 'string') {
    try { extra = JSON.parse(extra.extra); } catch (_) {}
  } else if (extra && extra.extra && typeof extra.extra === 'object') {
    extra = extra.extra;
  }
  return extra;
}

/**
 * Flutter injects `{ data: { deepLink, extra: <raw FCM data map> } }`, so the
 * flags that live in the FCM map (`isForeground`, `ring`, `reminder`) sit at
 * `data.data.extra`, not `data.data`. Resolve the map across the known shapes.
 */
export function extractPushData(data: any): any {
  const inner = data?.data?.extra;
  if (inner && typeof inner === 'object' && (inner.deepLink !== undefined || inner.isForeground !== undefined)) {
    return inner;
  }
  return data?.data ?? data ?? {};
}

/**
 * Open a deep link the way a notification tap does. Shared by the tap path and
 * the in-app banner so both behave identically.
 */
export function openDeepLink(navigate: NavigateFunction, deepLink: string, extra: any, rawData: any) {
  const currentPath = window.location.pathname;
  if (currentPath === deepLink) {
    // Already on the target page — the `state` payload below is consumed
    // immediately, so there's nothing to persist. Writing pendingDeepLink
    // here regardless of this branch used to leave a stale, unconsumed
    // entry in sessionStorage: LocalRoutes only re-checks it when
    // location.pathname changes, which it doesn't on this replace-to-
    // same-path navigation, so the entry survived until the user's NEXT,
    // unrelated navigation (e.g. pressing the header back button) — at
    // which point LocalRoutes saw "we left the pending path" and forced
    // the user right back here, making back-navigation look broken.
    navigate(deepLink, { state: { extra }, replace: true });
    try {
      window.dispatchEvent(new CustomEvent('nativeDeepLink', { detail: rawData }));
    } catch (_) {}
  } else {
    try {
      const storageValue = typeof extra === 'string' ? extra : JSON.stringify(extra);
      sessionStorage.setItem(`deeplink:${deepLink}`, storageValue);
    } catch (_) {}

    // Persist the intended destination so it survives a cold-start bootstrap
    // that would otherwise redirect to /home before this navigation settles.
    // A top-level guard (LocalRoutes) re-applies it once if it gets clobbered.
    try {
      sessionStorage.setItem(STORAGE_KEYS.PENDING_DEEP_LINK, JSON.stringify({ path: deepLink, applied: false }));
    } catch (_) {}

    navigate(deepLink, { state: { extra } });
  }
}

export const useGlobalNativeMessage = (navigate: NavigateFunction) => {
  useEffect(() => {
    const handleNativeMessage = (data: any) => {
      const deepLink = extractDeepLink(data);
      const pushData = extractPushData(data);
      const isForeground: boolean =
        pushData?.isForeground === 'true' || pushData?.isForeground === true;

      if (!deepLink) return;

      let extra = extractExtra(data);

      // Foreground guard-visitor notification → show in-app overlay, don't navigate.
      // Only the "visitor at gate" push carries `ring`; entered/exited/denied
      // pushes share the /visitor deep link and must not open the approval overlay.
      const isRingPush = !!pushData?.ring;
      if (isForeground && deepLink === '/visitor' && extra?.visitorUuid && isRingPush) {
        // A backend repeat while the resident is already on the visitor/approval
        // page (e.g. they tapped the notification) — they're handling it there.
        const isReminder = pushData?.reminder === 'true' || pushData?.reminder === true;
        if (isReminder && ['/visitor', '/approval'].includes(window.location.pathname)) return;
        try {
          window.dispatchEvent(
            new CustomEvent('visitorApprovalRequired', { detail: { extra } })
          );
        } catch (_) {}
        return;
      }

      // Foreground SOS → hand to GlobalSOSAlertOverlay, don't navigate.
      if (isForeground && extra?.sosId) {
        try {
          window.dispatchEvent(
            new CustomEvent('sosAlertRaised', { detail: { extra } })
          );
        } catch (_) {}
        return;
      }

      // Any other foreground push: never navigate (it could yank the user off a
      // half-filled form). Show a tappable banner and let open pages refresh.
      if (isForeground) {
        const { title, body } = getPushBannerContent(deepLink, extra);
        try {
          window.dispatchEvent(
            new CustomEvent('inAppPushBanner', { detail: { title, body, deepLink, extra } })
          );
          window.dispatchEvent(
            new CustomEvent('nativePushReceived', { detail: { deepLink, extra } })
          );
          // Pages already on this route (e.g. Home) refetch on this event.
          if (window.location.pathname === deepLink) {
            window.dispatchEvent(new CustomEvent('nativeDeepLink', { detail: data }));
          }
        } catch (_) {}
        return;
      }

      openDeepLink(navigate, deepLink, extra, data);
    };

    // Set real handler first, then drain queue — order matters so any
    // messages that arrive during the drain are handled immediately.
    (window as any).debugNativeMessage = handleNativeMessage;

    // Drain any messages queued before the app mounted (cold start path).
    try {
      const queued = ((window as any).__nativeQueue || []).splice(0);
      (window as any).__nativeQueue = [];
      // Deferred so sibling overlay/banner listeners (mounted after this effect) are attached
      if (queued.length) setTimeout(() => queued.forEach((m: any) => handleNativeMessage(m)), 0);
    } catch (_) {}

    return () => {
      (window as any).debugNativeMessage = null;
    };
  }, [navigate]);
};
